import React, { useMemo } from "react";
import Stack from "@mui/material/Stack";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import Link from "@mui/material/Link";
import { HgIndustryInput } from "./HgIndustryInput";
import { IndustriesPayload } from "./types";
import { NaicsInput } from "./NaicsInput";
import { SicInput } from "./SicInput";
import {
  INDUSTRY_TYPES,
  NAICS_HELP_URL,
  TOGGLE_BUTTON_OPTIONS,
} from "./constants";
import { useIndustrySelection } from "./hooks/useIndustrySelection";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import { LightTooltip } from "../../LightTooltip";
import CollectionSelect from "../../CollectionSelect/CollectionSelect";

interface IndustriesFilterProps {
  selectedValues: IndustriesPayload;
  onValuesChange: (payload: IndustriesPayload) => void;
  id?: string;
  showExcluded?: boolean;
}

const IndustriesFilterComponent: React.FC<IndustriesFilterProps> = ({
  selectedValues,
  onValuesChange,
  id = "industries-filter",
  showExcluded = false,
}) => {
  const {
    selectedIndustryType,
    industryIds,
    sicIds,
    naicsIds,
    excludedIds,
    excludedSicIds,
    excludedNaicsIds,
    handleIndustryTypeChange,
    handleHgIndustriesSelect,
    handleSicSelect,
    handleNaicsSelect,
    handleExcludedHgIndustriesSelect,
    handleExcludedSicSelect,
    handleExcludedNaicsSelect,
  } = useIndustrySelection({
    selectedValues,
    onValuesChange,
  });

  /**
   * Render the appropriate input component based on selected type
   */
  const renderIndustryInput = useMemo(() => {
    switch (selectedIndustryType) {
      case INDUSTRY_TYPES.INDUSTRY:
        return (
          <Stack direction="column" gap={0.5}>
            <HgIndustryInput
              id={`${id}-hg-industry-input`}
              selectedItems={industryIds}
              onIndustriesSelect={handleHgIndustriesSelect}
            />
            <CollectionSelect
              placeholder="Search collections"
              type="industry"
              onSelect={({ ids }) =>
                handleHgIndustriesSelect([
                  ...industryIds,
                  ...(Array.isArray(ids) ? ids : [])
                    .map((id) => Number(id))
                    .filter((id) => Number.isFinite(id))
                    .filter((id) => !industryIds.includes(id)),
                ])
              }
            />
          </Stack>
        );
      case INDUSTRY_TYPES.NAICS:
        return (
          <Stack direction="column" gap={0.5}>
            <NaicsInput
              id={`${id}-naics-input`}
              selectedItems={naicsIds}
              onNaicsSelect={handleNaicsSelect}
            />
            <CollectionSelect
              placeholder="Search collections"
              type="naics"
              onSelect={({ ids }) =>
                handleNaicsSelect([
                  ...naicsIds,
                  ...(ids as string[]).filter((id) => !naicsIds.includes(id)),
                ])
              }
            />
          </Stack>
        );
      case INDUSTRY_TYPES.SIC:
        return (
          <Stack direction="column" gap={0.5}>
            <SicInput
              id={`${id}-sic-input`}
              selectedItems={sicIds}
              onSicSelect={handleSicSelect}
            />
            <CollectionSelect
              placeholder="Search collections"
              type="sic"
              onSelect={({ ids }) =>
                handleSicSelect([
                  ...sicIds,
                  ...(ids as string[]).filter((id) => !sicIds.includes(id)),
                ])
              }
            />
          </Stack>
        );
    }
  }, [
    selectedIndustryType,
    industryIds,
    sicIds,
    naicsIds,
    handleHgIndustriesSelect,
    handleNaicsSelect,
    handleSicSelect,
    id,
  ]);

  /**
   * Render the appropriate excluded input component based on selected type
   */
  const renderExcludedIndustryInput = useMemo(() => {
    if (!showExcluded) return null;

    switch (selectedIndustryType) {
      case INDUSTRY_TYPES.INDUSTRY:
        return (
          <HgIndustryInput
            id={`${id}-excluded-hg-industry-input`}
            selectedItems={excludedIds || []}
            onIndustriesSelect={handleExcludedHgIndustriesSelect}
          />
        );
      case INDUSTRY_TYPES.NAICS:
        return (
          <NaicsInput
            id={`${id}-excluded-naics-input`}
            selectedItems={excludedNaicsIds || []}
            onNaicsSelect={handleExcludedNaicsSelect}
          />
        );
      case INDUSTRY_TYPES.SIC:
        return (
          <SicInput
            id={`${id}-excluded-sic-input`}
            selectedItems={excludedSicIds || []}
            onSicSelect={handleExcludedSicSelect}
          />
        );
    }
  }, [
    showExcluded,
    selectedIndustryType,
    excludedIds,
    excludedSicIds,
    excludedNaicsIds,
    handleExcludedHgIndustriesSelect,
    handleExcludedNaicsSelect,
    handleExcludedSicSelect,
    id,
  ]);

  // Memoize the toggle buttons to prevent unnecessary re-renders
  const toggleButtons = useMemo(
    () => (
      <ToggleButtonGroup
        id={`${id}-toggle-group`}
        size="small"
        color="secondary"
        value={selectedIndustryType}
        onChange={handleIndustryTypeChange}
        fullWidth
        exclusive
      >
        {TOGGLE_BUTTON_OPTIONS.map((industryType) => (
          <ToggleButton
            key={industryType}
            value={industryType}
            id={`${id}-toggle-${industryType.toLowerCase()}`}
          >
            <Stack direction="row" alignItems="center" gap={0.5}>
              {industryType}
              {selectedIndustryType === INDUSTRY_TYPES.NAICS &&
                industryType === INDUSTRY_TYPES.NAICS && (
                  <LightTooltip
                    title={
                      <Typography variant="body2">
                        You can see the complete 2012 NAICS list by visiting{" "}
                        <Link
                          href={NAICS_HELP_URL}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                        >
                          the official site.
                        </Link>
                      </Typography>
                    }
                  >
                    <HelpOutlineIcon fontSize="small" sx={{ ml: 0.5 }} />
                  </LightTooltip>
                )}
            </Stack>
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    ),
    [id, selectedIndustryType, handleIndustryTypeChange]
  );

  return (
    <Stack direction="column" gap={0.5} id={id}>
      {toggleButtons}
      <Stack id={`${id}-content`}>
        {showExcluded && <Typography variant="subtitle2">Include</Typography>}
        {renderIndustryInput}

        {showExcluded && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2">Exclude</Typography>
            {renderExcludedIndustryInput}
          </>
        )}
      </Stack>
    </Stack>
  );
};

// Export a memoized version of the component
export const IndustriesFilter = React.memo(IndustriesFilterComponent);
